import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Calendar, Plus, Pencil, Trash2, ChevronLeft, ChevronRight, CheckCircle2, Clock, Wand2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AY_ISIMLERI = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const GUN_ISIMLERI = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

const TIP_RENK = {
  genel: '#6b7280', otomatik: '#3b82f6', son_tarih: '#ef4444', toplanti: '#8b5cf6',
  hakedis: '#f59e0b', kabul: '#22c55e', denetim: '#06b6d4',
};

const emptyForm = { ihale_id: '', baslik: '', aciklama: '', tarih: '', bitis_tarihi: '', tip: 'genel', renk: '', tamamlandi: false };

export default function ISYTakvim({ ihaleler }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterIhale, setFilterIhale] = useState('all');

  const ayStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;

  const fetchTakvim = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/isy/takvim?ay=${ayStr}`;
      if (filterIhale !== 'all') url += `&ihale_id=${filterIhale}`;
      const res = await axios.get(url, { withCredentials: true });
      setItems(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [ayStr, filterIhale]);

  useEffect(() => { fetchTakvim(); }, [fetchTakvim]);

  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));

  const openCreate = (tarih) => {
    setFormData({ ...emptyForm, tarih: tarih || new Date().toISOString().split('T')[0] });
    setEditMode(false); setSelectedId(null); setDialogOpen(true);
  };

  const openEdit = (olay) => {
    setFormData({
      ihale_id: olay.ihale_id || '', baslik: olay.baslik || '',
      aciklama: olay.aciklama || '', tarih: olay.tarih || '',
      bitis_tarihi: olay.bitis_tarihi || '', tip: olay.tip || 'genel',
      renk: olay.renk || '', tamamlandi: olay.tamamlandi || false
    });
    setEditMode(true); setSelectedId(olay.id); setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.baslik.trim() || !formData.tarih) { toast.error('Başlık ve tarih zorunlu'); return; }
    setSaving(true);
    try {
      const payload = { ...formData };
      if (!payload.ihale_id) delete payload.ihale_id;
      if (!payload.renk) delete payload.renk;
      if (editMode) {
        await axios.put(`${API}/isy/takvim/${selectedId}`, payload, { withCredentials: true });
        toast.success('Güncellendi');
      } else {
        await axios.post(`${API}/isy/takvim`, payload, { withCredentials: true });
        toast.success('Olay eklendi');
      }
      setDialogOpen(false); fetchTakvim();
    } catch (e) { toast.error('Hata'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/isy/takvim/${deleteId}`, { withCredentials: true });
      toast.success('Silindi'); setDeleteId(null); fetchTakvim();
    } catch (e) { toast.error('Hata'); }
  };

  const handleOtomatik = async (ihaleId) => {
    try {
      const res = await axios.post(`${API}/isy/takvim/otomatik/${ihaleId}`, {}, { withCredentials: true });
      toast.success(res.data.message); fetchTakvim();
    } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
  };

  // Takvim grid oluştur
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startPad = (firstDay.getDay() + 6) % 7;
  const daysInMonth = lastDay.getDate();
  const today = new Date().toISOString().split('T')[0];

  const calendarDays = [];
  for (let i = 0; i < startPad; i++) calendarDays.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarDays.push(d);

  const getEventsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return items.filter(i => i.tarih === dateStr);
  };

  return (
    <div className="space-y-4" data-testid="isy-takvim">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-teal-400" />
          <h2 className="text-lg font-bold text-white">Takvim</h2>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filterIhale} onValueChange={setFilterIhale}>
            <SelectTrigger className="h-7 w-44 bg-gray-800 border-gray-700 text-white text-xs"><SelectValue placeholder="İhale" /></SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700">
              <SelectItem value="all" className="text-gray-300">Tüm İhaleler</SelectItem>
              {(ihaleler || []).map(ih => <SelectItem key={ih.id} value={ih.id} className="text-gray-300 text-xs">{ih.is_adi}</SelectItem>)}
            </SelectContent>
          </Select>
          {filterIhale !== 'all' && (
            <Button size="sm" variant="outline" className="h-7 border-gray-600 text-gray-300 text-xs" onClick={() => handleOtomatik(filterIhale)} data-testid="takvim-otomatik">
              <Wand2 className="w-3.5 h-3.5 mr-1" /> Otomatik
            </Button>
          )}
          <Button onClick={() => openCreate()} size="sm" className="bg-[#1e3a5f] hover:bg-[#2a4f7f] text-white h-7 text-xs" data-testid="takvim-add-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Olay Ekle
          </Button>
        </div>
      </div>

      {/* Ay Navigasyonu */}
      <div className="flex items-center justify-center gap-4">
        <Button size="sm" variant="ghost" onClick={prevMonth} className="text-gray-400 h-7"><ChevronLeft className="w-4 h-4" /></Button>
        <span className="text-sm font-semibold text-white min-w-[140px] text-center">{AY_ISIMLERI[month]} {year}</span>
        <Button size="sm" variant="ghost" onClick={nextMonth} className="text-gray-400 h-7"><ChevronRight className="w-4 h-4" /></Button>
      </div>

      {/* Takvim Grid */}
      <div className="grid grid-cols-7 gap-px bg-gray-800/50 rounded-lg overflow-hidden">
        {GUN_ISIMLERI.map(g => (
          <div key={g} className="bg-gray-900 py-1.5 text-center text-[10px] font-medium text-gray-500">{g}</div>
        ))}
        {calendarDays.map((day, i) => {
          const events = getEventsForDay(day);
          const dateStr = day ? `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` : '';
          const isToday = dateStr === today;
          return (
            <div key={i} className={`bg-gray-900/60 min-h-[70px] p-1 ${day ? 'cursor-pointer hover:bg-gray-800/40' : ''} transition-colors`}
              onClick={() => day && openCreate(dateStr)}>
              {day && (
                <>
                  <span className={`text-[10px] font-medium ${isToday ? 'bg-teal-500 text-white rounded-full w-5 h-5 flex items-center justify-center' : 'text-gray-400'}`}>
                    {day}
                  </span>
                  {events.slice(0, 3).map(ev => (
                    <div key={ev.id} className="mt-0.5 px-1 py-0.5 rounded text-[9px] truncate cursor-pointer"
                      style={{ background: (ev.renk || TIP_RENK[ev.tip] || '#6b7280') + '22', color: ev.renk || TIP_RENK[ev.tip] || '#6b7280' }}
                      onClick={(e) => { e.stopPropagation(); openEdit(ev); }}>
                      {ev.tamamlandi && <CheckCircle2 className="w-2.5 h-2.5 inline mr-0.5" />}
                      {ev.baslik}
                    </div>
                  ))}
                  {events.length > 3 && <div className="text-[8px] text-gray-500 px-1">+{events.length - 3}</div>}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Bu Ay Olaylar Listesi */}
      {items.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs text-gray-500 font-medium">{AY_ISIMLERI[month]} olayları ({items.length})</p>
          {items.map(ev => (
            <div key={ev.id} className="flex items-center gap-2 px-3 py-1.5 bg-gray-900/40 rounded hover:bg-gray-800/30 transition-colors" data-testid={`takvim-olay-${ev.id}`}>
              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: ev.renk || TIP_RENK[ev.tip] || '#6b7280' }} />
              <span className="text-xs text-gray-400 w-20 flex-shrink-0">{ev.tarih}</span>
              <span className={`text-xs flex-1 truncate ${ev.tamamlandi ? 'text-gray-500 line-through' : 'text-white'}`}>{ev.baslik}</span>
              <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-gray-400 hover:text-amber-400" onClick={() => openEdit(ev)}><Pencil className="w-2.5 h-2.5" /></Button>
              <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-gray-400 hover:text-red-400" onClick={() => setDeleteId(ev.id)}><Trash2 className="w-2.5 h-2.5" /></Button>
            </div>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader><DialogTitle className="text-white">{editMode ? 'Olayı Düzenle' : 'Yeni Takvim Olayı'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="col-span-2">
              <Label className="text-gray-400 text-xs">Başlık *</Label>
              <Input value={formData.baslik} onChange={e => setFormData({...formData, baslik: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" data-testid="takvim-form-baslik" />
            </div>
            <div><Label className="text-gray-400 text-xs">Tarih *</Label>
              <Input type="date" value={formData.tarih} onChange={e => setFormData({...formData, tarih: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" data-testid="takvim-form-tarih" />
            </div>
            <div><Label className="text-gray-400 text-xs">Bitiş Tarihi</Label>
              <Input type="date" value={formData.bitis_tarihi} onChange={e => setFormData({...formData, bitis_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" />
            </div>
            <div><Label className="text-gray-400 text-xs">Tip</Label>
              <Select value={formData.tip} onValueChange={v => setFormData({...formData, tip: v})}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  {Object.entries(TIP_RENK).map(([k]) => <SelectItem key={k} value={k} className="text-gray-300 text-xs">{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-gray-400 text-xs">İhale</Label>
              <Select value={formData.ihale_id || 'none'} onValueChange={v => setFormData({...formData, ihale_id: v === 'none' ? '' : v})}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  <SelectItem value="none" className="text-gray-300">Genel</SelectItem>
                  {(ihaleler || []).map(ih => <SelectItem key={ih.id} value={ih.id} className="text-gray-300 text-xs">{ih.is_adi}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="text-gray-400 text-xs">Açıklama</Label>
              <Input value={formData.aciklama} onChange={e => setFormData({...formData, aciklama: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-gray-400">İptal</Button>
            <Button onClick={handleSubmit} disabled={saving} className="bg-[#1e3a5f] hover:bg-[#2a4f7f] text-white" data-testid="takvim-form-save">
              {saving ? 'Kaydediliyor...' : editMode ? 'Güncelle' : 'Ekle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-gray-900 border-gray-700">
          <AlertDialogHeader><AlertDialogTitle className="text-white">Olayı Sil</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">Bu olayı silmek istediğinize emin misiniz?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 text-gray-300 border-gray-700">İptal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
